export const licensesMessage = {
  NO_LICENSE_SWITCH_MESSAGE_PREFIX: "Please ",
  NO_LICENSE_SWITCH_MESSAGE_LINK: "unsubscribe",
  NO_LICENSE_SWITCH_MESSAGE_SUFFIX: " to all apps on this stream to remove the license.",
  STARTER_EXTRA_FEATURE_NOT_SUPPORTED: (appName) => `${appName} app requires a higher license tier. Upgrade to enable it.`,
  STARTER_SWITCH_MESSAGE_PREFIX: "This stream currently exceeds the amount of apps permitted for a Starter license. Please ",
  STARTER_SWITCH_MESSAGE_LINK: "unsubscribe",
  STARTER_SWITCH_MESSAGE_SUFFIX: " to continue.",
  STARTER_SWITCH_MESSAGE_APPS_SUFFIX: " to apps in order to change this stream's license type.",
  STARTER_SWITCH_CVAPP_PREFIX: "This stream is subscribed to Extra Features apps that are only available with Intelligence and Search licenses. Please ",
  STARTER_SWITCH_EXTRA_FEATURES_SUFFIX: " to any Extra Features apps and try again.",
  STARTER_SWITCH_COMBINED_CVAPP_PREFIX:
    "This stream currently exceeds the amount of apps permitted for a Starter license and is subscribed to Extra Features apps that are only available with Intelligence and Search licenses. Please ",
  ZERO_LICENSE_AVAILABLE_MESSAGE: "No licenses available!",
  LICENSE_SUSPENDED_MESSAGE: "Your account has been cancelled. All licenses are suspended.",
  LICENSE_OVERUSE_MESSAGE:
    "There are more licenses deployed than paid for. Please downgrade or unsubscribe from licenses. If no action is made, your access may be interrupted and you may be charged for these additional licenses.",
  UPGRADE_LICENSE: "You have successfully upgraded your license.",
  EXTRAFEATURE_TOOLTIP: "This extra feature app is already subscribed and can only be used once."
};

export const LICENSE_OVERUSE_MODAL_TEXTS = {
  LICENSE_LIMIT_EXCEEDED_TITLE: "License limit exceeded",
  EXCEEDED_LIMIT_HEADING: "You've exceeded your license limit!",
  EXCEEDED_LIMIT_BODY: (used, paid) => `Your team is currently using ${used} Active Licenses, but your current plan only included ${paid} Paid Licenses.`,
  CHARGE_WARNING:
    "To avoid being charged for the additional licenses, please navigate to the license management page to review and remove oversubscribed licenses."
};

// License labels
export const LICENSE_LABELS = {
  LICENSE_UNLIMITED: (displayName) => `${displayName} (Unlimited Apps)`,
  STARTER_LIMITED: (displayName) => `${displayName} (Limit per license: 3 apps)`,
  NO_LICENSE: "No license",
  REMOVE_LICENSE_DESC: "Remove the license from this camera stream",
  SELECT_LICENSE: "Select a license to subscribe to apps",
  AVAILABLE_SUFFIX: "available",
  LICENSE_TYPE_PREFIX: (licenseVersion) => `License type: ${licenseVersion}`
};

// SessionStorage keys
export const SESSION_STORAGE_KEYS = {
  KEEP_LICENSE_DATA: "keepLicenseData",
  SELECTED_LICENSE_RECORD: "selectedLicenseRecord",
  LICENSES: "licenses",
  BILLING_ACCOUNTS: "billingAccounts",
  PENDING_SEARCH_TERM: "pendingSearchTerm"
};

// Modal texts
export const LICENSE_MODAL_TEXTS = {
  CHANGE_LICENSE_TYPE: "Change license type",
  CURRENTLY_SELECTED: "Current active license",
  YES_CHANGE: "Yes, change",
  NO_CANCEL: "No, cancel",
  UPGRADE_LICENSE: "Upgrade license",
  YES_UPGRADE: "Yes, upgrade"
};

// Permission types
export const LICENSE_PERMISSION = {
  TYPE: "License management"
};

// Notification types
export const LICENSE_NOTIFICATION_TYPES = {
  NO_PERMISSION: "No Permission",
  SUSPEND_FAILED: "Suspend License failed",
  RESUME_FAILED: "Resume License failed",
  NO_LICENSE_AVAILABLE: "No available License",
  CHANGE_LICENSE_FAILED: "Change License Failed",
  LICENSE_CHANGE_SUCCESS: "License type change success",
  LICENSE_SUSPENDED: "License Suspended"
};

// Notification messages
export const LICENSE_NOTIFICATION_MESSAGES = {
  LICENSE_REMOVED_SUCCESS: "License is removed successfuly",
  LICENSE_CHANGED_SUCCESS: "The selected license has been changed",
  LICENSE_REMOVE_FAILED: "Failed to remove license, please try again",
  LICENSE_SWITCH_FAILED: "Failed to switch license, please try again",
  INCOMPATIBLE_SKU_ERROR: "Please ensure the 'Stream Monitoring' and/or 'Object Blurring' applications are unsubscribed from the camera.",
  BILLING_ACCOUNTS_FETCH_FAILED: "Failed to get Billing Accounts, please reload the page.",
  LICENSES_FETCH_FAILED: "Failed to get Licenses, please reload the page.",
  LICENSE_ASSIGN_SUCCESS: "License has been successfully assigned to the device.",
  LICENSE_ASSIGN_FAILURE: "Error while assigning license. Please try again.",
  LICENSE_SUSPENDED_MESSAGE: "The license for this stream has been suspended. Please activate the license for this stream to manage apps."
};

// Error descriptions
export const LICENSE_ERROR_DESCRIPTIONS = {
  INCOMPATIBLE_SKU:
    "action cannot be executed on the device, the sku to be assigned is not compatible to the sku to be removed due to allowed cvApps configuration.",
  API_ERROR: "Error fetching licenses:",
  FETCH_FOV_ERROR: "Failed to fetch the latest streams data.",
  FETCH_STREAMS_FAILED: "Failed to fetch streams",
  FETCH_STREAMS_FAILED_INFO: "An error occurred while fetching streams for the selected license. Please try again later."
};

// Action types
export const LICENSE_ACTION_TYPES = {
  PUT_BILLING_ACCOUNTS_LICENSES: "PUT_BILLING_ACCOUNTS_LICENSES",
  PUT_LICENSES_OF_ACCOUNT: "PUT_LICENSES_OF_ACCOUNT",
  PUT_LICENSES_OF_ACCOUNT_BY_ID: "PUT_LICENSES_OF_ACCOUNT_BY_ID",
  FETCH_BILLING_ACCOUNTS_LICENSES: "FETCH_BILLING_ACCOUNTS_LICENSES",
  FETCH_LICENSES_OF_ACCOUNT: "FETCH_LICENSES_OF_ACCOUNT",
  FETCH_LICENSES_OF_ACCOUNT_BY_ID: "FETCH_LICENSES_OF_ACCOUNT_BY_ID",
  CHANGE_LICENSE_TYPE: "CHANGE_LICENSE_TYPE",
  ASSIGN_LICENSE: "ASSIGN_LICENSE",
  ASSIGN_LICENSE_SUCCESS: "ASSIGN_LICENSE_SUCCESS",
  ASSIGN_LICENSE_FAILURE: "ASSIGN_LICENSE_FAILURE",
  SET_FOV_BY_LICENSE_CACHE: "SET_FOV_BY_LICENSE_CACHE",
  INVALIDATE_FOV_BY_LICENSE_CACHE: "INVALIDATE_FOV_BY_LICENSE_CACHE",
  SET_SELECTED_LICENSE_RECORD: "SET_SELECTED_LICENSE_RECORD",
  BATCH_LICENSE_ACTIONS: "BATCH_LICENSE_ACTIONS"
};

export const LICENSE_TYPES = {
  INTELLIGENCE: "Intelligence",
  STARTER: "Starter",
  STANDARD: "standard",
  ADDON: "addon",
  NO_LICENSE: "No license",
  INTELLIGENCE_V2: "Intelligence",
  LITE_V2: "Starter",
  ENROLLED_APPEARANCE_V2: "Enrolled Appearance",
  FORENSIC_SEARCH_V2: "Search"
};

// SKU values for license type detection
export const LICENSE_SKU_PATTERNS = {
  LITE: "VI_AI_LITE_LIC_MON_A",
  INTELLIGENCE: "VI_AI_INT_LIC_MON_A",
  INTELLIGENCE_V2: "VI_AI_INT_LIC_MON_V2",
  LITE_V2: "VI_AI_LITE_LIC_MON_V2",
  ENROLLED_APPEARANCE_V2: "VI_AI_EA_LIC_MON_A",
  FORENSIC_SEARCH_V2: "VI_FOR_LIC_MON_A"
};

// Base (non-addon) license SKUs — v1 and v2. Used to identify the stream's primary CV license.
export const BASE_LICENSE_SKUS = [
  LICENSE_SKU_PATTERNS.INTELLIGENCE,
  LICENSE_SKU_PATTERNS.LITE,
  LICENSE_SKU_PATTERNS.INTELLIGENCE_V2,
  LICENSE_SKU_PATTERNS.LITE_V2,
  LICENSE_SKU_PATTERNS.FORENSIC_SEARCH_V2
];

// SKUs that belong to the v2 license family.
const V2_SKUS = [
  LICENSE_SKU_PATTERNS.INTELLIGENCE_V2,
  LICENSE_SKU_PATTERNS.LITE_V2,
  LICENSE_SKU_PATTERNS.ENROLLED_APPEARANCE_V2,
  LICENSE_SKU_PATTERNS.FORENSIC_SEARCH_V2
];

// Returns true if the SKU belongs to the v2 license family.
export const isV2Sku = (sku) => typeof sku === "string" && V2_SKUS.includes(sku);

// Returns true when a v1-only-gated feature (Search, Enrolled Appearance) is
// accessible: its feature flag is on AND the account isn't v1-only. Used to
// guard both nav entry points and their routes from direct URL access.
export const isFeatureAccessible = (isFlagEnabled, isV1OnlyAccount) => Boolean(isFlagEnabled) && isV1OnlyAccount === false;

// Returns the primary base license SKU from a stream's licenses array.
// A stream holds at most one primary license at a time (add-on licenses like
// Enrolled Appearance may also be present but are not returned here).
export const getActiveLicenseSku = (licenses) => {
  if (!Array.isArray(licenses) || licenses.length === 0) return "";
  return licenses.find((sku) => sku && BASE_LICENSE_SKUS.includes(sku)) || "";
};

// License values
export const LICENSE_VALUES = {
  NO_LICENSE: "nolicense"
};

// License status
export const LICENSE_STATUS = {
  ACTIVE: "active",
  SUSPENDED: "suspended",
  EXPIRED: "expired",
  GRACE_PERIOD: "grace_period"
};

// License status display labels
export const LICENSE_STATUS_LABELS = {
  ACTIVE: "Active",
  SUSPENDED: "Suspended"
};

// Action/permission values
export const LICENSE_ACTIONS = {
  EDIT: "Edit"
};

// UI Text
export const LICENSE_UI_TEXT = {
  SETTINGS: "Settings",
  LICENSES: "Licenses",
  CAMERA: "Camera",
  STREAM: "Stream",
  SITE: "Site",
  COMPANION: "Companion",
  APPS: "Apps",
  CHANGE_LICENSE: "Change license",
  LICENSE_STATUS: "License status",
  NA: "N/A",
  APPS_SUFFIX: "Apps",
  BILLING_ACCOUNT_PREFIX: "Billing account: ",
  LICENSE: "License",
  PURCHASED: "Purchased",
  USED: "Used",
  AVAILABLE: "Available",
  SITES: "Sites",
  SELECT_BILLING_ACCOUNT: "Select a billing account",
  NO_BILLING_ACCOUNTS: "No billing accounts available",
  LICENSE_OVERUSE_TAG: "License overuse",
  LICENSES_SUSPENDED_TAG: "Licenses suspended",
  LICENSE_OVERUSED_TAG: "License overused"
};

// Console warning messages
export const LICENSE_WARNINGS = {
  COMPANION_ID_NOT_PROVIDED: "Companion ID not provided",
  COMPANION_DEVICE_NOT_FOUND: "Companion device not found",
  CAMERA_ID_NOT_PROVIDED: "Camera ID not provided",
  CAMERA_DATA_NOT_FOUND: "Camera data not found",
  STREAM_ID_NOT_PROVIDED: "Stream ID not provided",
  FOV_OBJECT_NOT_FOUND: "FOV object not found",
  WARNING: "warning",
  NO_DATA: "No data available",
  NO_DATA_INFO: "No streams available for the selected license."
};

// Device field keys
export const DEVICE_FIELDS = {
  NAME: "name",
  ID: "id"
};

export const SKU_TO_APP_MAPPING = {
  THINGSPACE_SENSE_SAAS: "Sensor Insights",
  "VZ-CAT-PLAT-LOS": "AssetTracking"
};

export const VISION_SKUS_DISPLAYNAME = {
  VI_AI_INT_LIC_MON_A: "Intelligence",
  VI_AI_LITE_LIC_MON_A: "Starter",
  VI_AI_INT_LIC_MON_V2: "Intelligence",
  VI_AI_LITE_LIC_MON_V2: "Starter",
  VI_AI_EA_LIC_MON_A: "Enrolled Appearance",
  VI_FOR_LIC_MON_A: "Search"
};

// ROI delete saga response statuses
export const ROI_DELETE_STATUS = {
  SUCCESS: "Success",
  ERROR: "Error"
};

// Origin page identifiers used for navigation context across ROI / cvApp flows
export const ORIGIN_PAGES = {
  CV_APP_SUB: "cvAppSub",
  CAMERA_DETAILS: "cameraDetails",
  LICENSE_DETAILS: "licenseDetails"
};

// ROI notification banner types
export const ROI_NOTIFICATION_TYPES = {
  ROI_DELETE: "ROI Delete",
  UNDEPLOY_CV_APP: "Undeploy CV App",
  UNDEPLOY_FAILED: "Undeploy failed"
};

// Unsubscribe / Edit-apps flow UI text & banner messages
export const UNSUBSCRIBE_FLOW_TEXTS = {
  EDIT_APPS_BREADCRUMB: "Edit Apps",
  UNSUBSCRIBE_APPS_BREADCRUMB: "Unsubscribe Apps",
  CV_APP_CONFIG_BREADCRUMB: "CV App Configuration",
  CAMERAS_BREADCRUMB: "Cameras",
  CAMERA_FALLBACK: "Camera",
  UNSUBSCRIBE_SUCCESS: "You have successfully unsubscribed the selected app.",
  UNSUBSCRIBE_FAILURE: "Failed to unsubscribe selected app, please try again",
  ROI_DELETE_SUCCESS: "You have successfully deleted the selected region of interest.",
  ROI_DELETE_FAILURE: "Failed to Delete the ROI. Please try again later",
  UNDEPLOY_SUCCESS: "You have successfully undeployed from the app",
  UNDEPLOY_FAILURE: "Undeploy CV app failed. Please try again later"
};

// cvAppSub "Maximum application limit" modal text
export const MAX_APP_LIMIT_MODAL = {
  TITLE: "Maximum application limit",
  CAPACITY_MESSAGE: (limit) => `This stream has hit the maximum capacity of ${limit} apps. `,
  UNSUBSCRIBE_LINK: "unsubscribe",
  OR: " or ",
  FROM_EXISTING_APPS: " from existing apps.",
  SELECT_MORE_SUFFIX: " to select more apps beyond the limit.",
  CLOSE: "Close"
};

export const LICENSE_ORIGIN = {
  NEW_APP: "NewApp",
  CV_APPS: "CvApps",
  LICENSE: "License",
  POLYGON: "Polygon"
};

// Default width for the billing account dropdown on tablet/desktop
export const DROPDOWN_DEFAULT_WIDTH = "328px";
